/**
 * A responsive primary/inspector workspace (#1466 / ADR F-30).
 *
 * This component owns composition only. The host owns the selected record,
 * governed reads and writes, routing, and persistence of the user's preferred
 * width. No inspector value means no inspector column: empty chrome must never
 * tax the working canvas. On a narrow container an open inspector gets the
 * canvas rather than squeezing two unusable columns beside one another.
 *
 * ── ONE separator, two geometries (#1466 mailbox 47851, ruling B1) ──────────
 * "generalise WorkspaceSplit: yes. One accessible separator/state machine,
 * parameterised geometry (percent or bounded px); do not create a second resize
 * implementation."
 *
 * The measurement that forced the ruling: the separator was 52 of this file's
 * 110 lines before this change — `Splitter` (lines 33-82) plus the `clamp` it
 * depended on (30-31). Those 52 lines are pointer capture, the
 * drag/preview/commit state machine, the keyboard model, double-click reset,
 * and four ARIA attributes that all have to stay truthful together. The global
 * drawer wants exactly that control at `clamp(420px, 30vw, 640px)`, and a copy
 * of those 52 lines is a second implementation that drifts on the first
 * accessibility fix.
 *
 * So the UNIT became a value the caller supplies. What the type had to carry is
 * the MIDDLE term: `clamp(420px, 30vw, 640px)` is a floor, a preferred width
 * that depends on the window, and a ceiling — and a plain `{min, max}` in
 * pixels cannot say `30vw`. Hence `SplitPreferred`. A percent geometry needs no
 * such term because a percent is ALREADY relative to the container, which is
 * why the two halves of the union spell `preferred` differently: the unit
 * decides whether the middle term needs a second reference at all.
 *
 * The viewport term is resolved in JS, not left as a CSS `clamp()`, because
 * `aria-valuenow` has to state a number. A separator that paints `30vw` and
 * announces "30" is lying in whichever unit it is not using, and that is the
 * failure this generalisation is most likely to produce silently.
 *
 * Percent callers are untouched: omit `geometry` and this is the component
 * `ListView` has always rendered, down to the 2 / 10 keyboard steps.
 *
 * The px geometry is deliberately NOT on the `@solum/ui` barrel yet.
 * `conformance/inventory.test.ts:211` asserts the derived helper set equals its
 * hand-written `HELPERS` list exactly (`toEqual`, not a superset), so exporting
 * `DRAWER_PX_GEOMETRY`, `clampToGeometry` or `resolvePreferred` from
 * `shell/index.ts` reds that test until the same change extends `HELPERS`. That
 * is a two-package edit and belongs with the drawer follow-up that consumes
 * them, not with this one. Everything here is reachable via the module path in
 * the meantime.
 */
import {
  useCallback, useRef, useSyncExternalStore,
  type CSSProperties, type KeyboardEvent, type PointerEvent, type ReactNode,
} from 'react'
import s from './WorkspaceSplit.module.css'

export const MIN_INSPECTOR_PERCENT = 25
export const MAX_INSPECTOR_PERCENT = 75
export const DEFAULT_INSPECTOR_PERCENT = 45

/**
 * The middle term of `clamp(min, preferred, max)` for a PIXEL geometry.
 *
 * `fixed` is a constant number of pixels. `viewport` is the `Nvw` term — a
 * fraction of the window, resolved at render so the value announced and the
 * value painted are the same number.
 */
export type SplitPreferred =
  | { kind: 'fixed'; value: number }
  | { kind: 'viewport'; fraction: number }

interface SplitGeometryBase {
  /** The floor, in `unit`. */
  min: number
  /** The ceiling, in `unit`. */
  max: number
  /** Arrow-key resize step, in `unit`. */
  step: number
  /** Shift-arrow resize step, in `unit`. */
  coarseStep: number
}

/** A share of the CONTAINER. The middle term is a constant: `%` is relative. */
export interface PercentGeometry extends SplitGeometryBase {
  unit: 'percent'
  preferred: number
}

/** Bounded pixels. The middle term may depend on the window — `30vw`. */
export interface PxGeometry extends SplitGeometryBase {
  unit: 'px'
  preferred: SplitPreferred
}

export type SplitGeometry = PercentGeometry | PxGeometry

/** What every existing caller gets, unchanged: 25 % … 45 % … 75 %. */
export const PERCENT_GEOMETRY: PercentGeometry = {
  unit: 'percent',
  min: MIN_INSPECTOR_PERCENT,
  max: MAX_INSPECTOR_PERCENT,
  preferred: DEFAULT_INSPECTOR_PERCENT,
  step: 2,
  coarseStep: 10,
}

/**
 * `clamp(420px, 30vw, 640px)` — the global drawer's geometry.
 *
 * 420 is the floor a two-column form stops working below; 640 is the ceiling
 * past which the drawer stops being a drawer. Steps are 16 / 64 rather than
 * 2 / 10 because a 2px arrow press is not a resize anybody can see.
 */
export const DRAWER_PX_GEOMETRY: PxGeometry = {
  unit: 'px',
  min: 420,
  max: 640,
  preferred: { kind: 'viewport', fraction: 0.3 },
  step: 16,
  coarseStep: 64,
}

/** The ONE clamp. Applied before paint and again before every commit. */
export const clampToGeometry = (geometry: SplitGeometry, value: number) =>
  Math.min(geometry.max, Math.max(geometry.min, Math.round(value)))

/**
 * The geometry's default width, in its own unit, already inside the bounds.
 *
 * `viewportWidth` is ignored by every geometry whose middle term is a constant,
 * so the caller may pass 0 when it has no window to read.
 */
export function resolvePreferred(geometry: SplitGeometry, viewportWidth: number): number {
  const raw = geometry.unit === 'percent'
    ? geometry.preferred
    : geometry.preferred.kind === 'fixed'
      ? geometry.preferred.value
      : geometry.preferred.fraction * viewportWidth
  return clampToGeometry(geometry, raw)
}

/** Does this geometry's default need the window measured? */
const readsViewport = (geometry: SplitGeometry) =>
  geometry.unit === 'px' && geometry.preferred.kind === 'viewport'

const cssLength = (geometry: SplitGeometry, value: number) =>
  geometry.unit === 'percent' ? `${value}%` : `${value}px`

/** How the unit reads out loud in `aria-valuetext`. */
const unitWord = (geometry: SplitGeometry) =>
  geometry.unit === 'percent' ? 'percent' : 'pixels'

export interface WorkspaceSplitProps {
  primary: ReactNode
  /** Omit/null to collapse the inspector and its divider completely. */
  inspector?: ReactNode
  inspectorLabel: string
  inspectorId?: string
  /** Percent of the container unless the caller says otherwise. */
  geometry?: SplitGeometry
  /** The inspector's width, in `geometry.unit`. */
  inspectorSize?: number
  /** The percent-geometry spelling of `inspectorSize`; `inspectorSize` wins. */
  inspectorPercent?: number
  /** Live drag, in `geometry.unit` — the host decides whether to persist. */
  onInspectorPreview?: (size: number) => void
  onInspectorCommit?: (size: number) => void
  onInspectorReset?: () => void
  testId?: string
}

/**
 * THE separator. Exported because #1465's drawer needs one and the ruling
 * (mailbox 47851 B1) is "one accessible separator/state machine, parameterised
 * geometry; do not create a second resize implementation" — which is only
 * possible if the thing is reachable from outside this file.
 *
 * Nothing about the mechanics changed to export it. It was already
 * geometry-agnostic; the only thing that was inspector-specific was the two
 * strings, so `subject` supplies them. A separator that announces "Inspector
 * 420 pixels" while sitting on a drawer is not a shared control, it is a shared
 * control with somebody else's label on it.
 *
 * `container` is whichever box the pointer arithmetic should measure from:
 * `box.right - clientX`. For the workspace that is the split; for the drawer it
 * is the PANEL, whose right edge is pinned by the rail so the panel widens
 * leftward under the cursor.
 */
export function Splitter({ geometry, size, preferred, container, subject = 'Inspector', onPreview, onCommit, onReset }: {
  geometry: SplitGeometry
  size: number
  /** The resolved default, so "(default)" is said about the width in play. */
  preferred: number
  container: React.RefObject<HTMLDivElement | null>
  /** What this separator resizes, for its label and value text. Capitalised. */
  subject?: string
  onPreview?: (size: number) => void
  onCommit?: (size: number) => void
  onReset?: () => void
}) {
  /**
   * A press is not a drag until the pointer MOVES (#1465, ruling 48342 (2)).
   *
   * `pointerEnd` used to commit unconditionally, so a plain click on the
   * separator wrote a width — and a DOUBLE click wrote two, both after the
   * `dblclick` that was supposed to reset. Measured on the drawer: after a
   * double click the stored width was `580`, a cursor-derived number, not
   * `null`. Reset was reachable only through the keyboard.
   *
   * The order is the trap. `dblclick` fires after the second `pointerup`, so
   * "reset last wins" is the natural assumption and it is wrong in the other
   * direction: the reset lands, and then nothing overwrites it — but the two
   * commits before it have already written, so the LAST WRITE is the reset yet
   * the stored value is whatever the second commit left when the reset is a
   * removal and the commits are writes to the same key in the same tick.
   *
   * Rather than sequence them, remove the cause: a press that never moved is
   * not a resize and has nothing to commit.
   */
  const dragging = useRef(false)
  const moved = useRef(false)
  const fromPointer = (clientX: number) => {
    const box = container.current?.getBoundingClientRect()
    if (!box?.width) return size
    const raw = geometry.unit === 'percent'
      ? ((box.right - clientX) / box.width) * 100
      : box.right - clientX
    return clampToGeometry(geometry, raw)
  }
  const pointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    event.preventDefault()
    dragging.current = true
    moved.current = false
    event.currentTarget.setPointerCapture?.(event.pointerId)
  }
  const pointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return
    moved.current = true
    onPreview?.(fromPointer(event.clientX))
  }
  const pointerEnd = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return
    dragging.current = false
    try { event.currentTarget.releasePointerCapture?.(event.pointerId) } catch { /* already released */ }
    // A press with no movement is a click, not a resize. Committing it is what
    // made double-click reset unreachable by pointer.
    if (!moved.current) return
    moved.current = false
    onCommit?.(fromPointer(event.clientX))
  }
  const keyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? geometry.coarseStep : geometry.step
    /* THE SEPARATOR is the controlled object, not the pane (#1465, ruling
       48342 (1)). `ArrowLeft` moves the separator LEFT — and because the pane
       it sizes is the one on its right, moving it left makes that pane BIGGER.
       `ArrowRight` narrows it.

       This was inverted, and it was inverted against its own pointer: the drag
       is `box.right - clientX`, so pulling the divider left has always widened
       the pane. So a mouse user and a keyboard user got opposite results from
       the same nominal direction, and the keyboard one also contradicted
       WAI-ARIA's window-splitter pattern, which describes Left Arrow as moving
       the separator left. */
    if (event.key === 'ArrowLeft') { event.preventDefault(); onCommit?.(clampToGeometry(geometry, size + step)) }
    else if (event.key === 'ArrowRight') { event.preventDefault(); onCommit?.(clampToGeometry(geometry, size - step)) }
    else if (event.key === 'Home') { event.preventDefault(); onReset?.() }
  }
  return (
    // ARIA separator is the native resize-control pattern; pointer and keyboard
    // gestures are both implemented immediately below.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div className={s.divider} data-testid="split-divider" role="separator"
      aria-orientation="vertical" aria-label={`Resize the ${subject.toLowerCase()}`}
      aria-valuenow={size} aria-valuemin={geometry.min}
      aria-valuemax={geometry.max}
      aria-valuetext={`${subject} ${size} ${unitWord(geometry)}${size === preferred ? ' (default)' : ''}`}
      tabIndex={0} onPointerDown={pointerDown} onPointerMove={pointerMove}
      onPointerUp={pointerEnd} onPointerCancel={pointerEnd} onKeyDown={keyDown}
      onDoubleClick={onReset} title="Drag to resize · double-click to reset">
      <span className={s.grip} aria-hidden="true" />
    </div>
  )
}

export function WorkspaceSplit({
  primary, inspector, inspectorLabel, inspectorId,
  geometry = PERCENT_GEOMETRY, inspectorSize, inspectorPercent,
  onInspectorPreview, onInspectorCommit, onInspectorReset, testId = 'workspace-split',
}: WorkspaceSplitProps) {
  const container = useRef<HTMLDivElement>(null)
  // Only a viewport-relative default subscribes; every other geometry keeps a
  // constant snapshot, so percent callers gain no listener and no re-render.
  const live = readsViewport(geometry)
  const subscribe = useCallback((onChange: () => void) => {
    if (!live || typeof window === 'undefined') return () => { /* nothing to watch */ }
    window.addEventListener('resize', onChange)
    return () => { window.removeEventListener('resize', onChange) }
  }, [live])
  const viewportWidth = useSyncExternalStore(
    subscribe,
    () => (live && typeof window !== 'undefined' ? window.innerWidth : 0),
    () => 0,
  )
  const preferred = resolvePreferred(geometry, viewportWidth)
  const open = inspector != null
  const size = clampToGeometry(geometry, inspectorSize ?? inspectorPercent ?? preferred)
  const resizable = onInspectorCommit != null && onInspectorReset != null
  const style = { '--workspace-inspector-pct': cssLength(geometry, size) } as CSSProperties
  return (
    /* `.frame` exists ONLY to be the query container for the narrow-width rule
       whose subject is `.split` — see the stylesheet. The ref, the testid and
       `data-open` all stay on the grid, so the resize maths and every existing
       selector are untouched. */
    <div className={s.frame}>
      <div ref={container} className={s.split} data-open={open} data-testid={testId} style={style}>
        <div className={s.primary}>{primary}</div>
        {open && (
          <>
            {resizable
              ? <Splitter geometry={geometry} size={size} preferred={preferred} container={container}
                  onPreview={onInspectorPreview} onCommit={onInspectorCommit} onReset={onInspectorReset} />
              : <div className={s.boundary} aria-hidden="true" />}
            <aside id={inspectorId} className={s.inspector} aria-label={inspectorLabel}>
              {inspector}
            </aside>
          </>
        )}
      </div>
    </div>
  )
}
